/**
 * The printed notes page (#157) — every note's full text, numbered globally
 * and grouped under the part it points at.
 *
 * Its own page, before the instructions, because a note is something the maker
 * wrote to be READ, and reading it off the pattern means hunting across tiles
 * for cards small enough not to cover the geometry. The numbers are what tie
 * the two together: the card on the pattern carries the number, this page
 * carries the words.
 *
 * Grouping by part rather than one flat list because a note almost always
 * belongs to a step on one piece, and "Front panel: 1, 4" is how a maker holds
 * the work in their head. Notes attached to nothing — and notes whose part has
 * since been deleted — fall to a general heading at the end rather than being
 * dropped.
 *
 * Under the heading, a note that points at one of the part's cutouts or seams
 * names it (#1076). The part alone is not enough to act on: "8 SPI here" under
 * "Front panel" leaves the maker to guess which of five seams, and the badge on
 * the drawing is the only other way to find out — which is exactly the trip
 * this page exists to save.
 */
import type { jsPDF } from "jspdf";
import type { Project } from "../model/Project";
import { groupNotesByPart, wrapNoteText } from "../model/notes";

/** Font sizes in pt for the page furniture and the note bodies. */
const TITLE_PT = 18;
const GROUP_PT = 12;
const BODY_PT = 10;

export function hasPrintableNotes(project: Project): boolean {
  return groupNotesByPart(project).length > 0;
}

/**
 * Render the notes page(s). Returns without touching the document when there
 * is nothing to print, so an empty section never costs a blank sheet.
 *
 * `nextPage` and `drawWatermark` are passed in rather than imported to keep
 * this module free of the pdfExporter's page-state bookkeeping — it renders,
 * the caller owns when a page begins.
 */
export function renderNotesPage(
  doc: jsPDF,
  project: Project,
  safe: { x: number; y: number; w: number; h: number },
  beginPage: () => void,
): void {
  const groups = groupNotesByPart(project);
  if (groups.length === 0) return;

  const bottom = safe.y + safe.h;
  beginPage();

  doc.setFontSize(TITLE_PT);
  doc.setTextColor(20);
  doc.text("Notes", safe.x, safe.y + 8);

  let y = safe.y + 20;

  for (const group of groups) {
    // Keep a heading with at least its first line rather than stranding it at
    // the foot of a page.
    if (y + 14 > bottom) {
      beginPage();
      y = safe.y + 10;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(GROUP_PT);
    doc.setTextColor(20);
    doc.text(group.title, safe.x, y);
    y += 7;

    for (const { note, number, on } of group.notes) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(BODY_PT);
      doc.setTextColor(60);

      const label = `${number}.`;
      const labelW = doc.getTextWidth(label) + 2;

      // What on this part the note names, on the number's own line: it reads as
      // a sub-heading for the note, and putting it inline with the text would
      // let it disappear into a wrapped paragraph.
      if (on) {
        if (y > bottom) {
          beginPage();
          y = safe.y + 10;
        }
        doc.setFont("helvetica", "bold");
        doc.setTextColor(20);
        doc.text(label, safe.x, y);
        doc.text(on, safe.x + labelW, y);
        y += 5;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(60);
      }
      // Wrap to the column left of the number gutter, measured with the SAME
      // font the text is drawn in — the shared wrapper takes a measurer for
      // exactly this reason, so screen and page break lines in the same place.
      const lines = wrapNoteText(note.text, safe.w - labelW, (s) =>
        doc.getTextWidth(s),
      );
      const printed = lines.length > 0 ? lines : [""];

      for (let i = 0; i < printed.length; i++) {
        if (y > bottom) {
          beginPage();
          y = safe.y + 10;
        }
        if (i === 0 && !on) doc.text(label, safe.x, y);
        doc.text(printed[i]!, safe.x + labelW, y);
        y += 5;
      }
      y += 2;
    }
    y += 4;
  }
}
